import { useEffect, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { CheckCircle, Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

import { Button } from "src/components/ui/button";
import { Form } from "src/components/ui/form";
import { InfoBox } from "src/components/ui/info-box";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "src/components/ui/responsive-dialog";
import { useApiMutation } from "src/lib/hooks/useApi";

const MessageDialog = ({
  formSchema,
  atom,
  mutation,
  title,
  infoBoxText,
  defaultValues,
  children,
  submitMapping = (data) => data,
}) => {
  const [dialogState, setDialogState] = useAtom(atom);
  const [submitted, setSubmitted] = useState(false);
  const baseDefaultValuesRef = useRef(defaultValues);

  const { mutationApi, loading } = useApiMutation({
    mutation,
    onSuccess: () => {
      setSubmitted(true);

      toast.success("Message sent successfully!");
    },
    onError: () =>
      "There was a problem sending your message. Please try again.",
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: baseDefaultValuesRef.current,
  });

  const { open, prefillValues, focusField } = dialogState;

  useEffect(() => {
    if (!open) return;

    form.reset({
      ...baseDefaultValuesRef.current,
      ...(prefillValues || {}),
    });

    if (focusField) {
      const timerId = window.setTimeout(() => {
        form.setFocus(focusField);
      }, 0);
      return () => window.clearTimeout(timerId);
    }
  }, [focusField, form, open, prefillValues]);

  const setOpen = (open) => {
    setDialogState({
      ...dialogState,
      open,
      prefillValues: open ? prefillValues : undefined,
      focusField: open ? focusField : undefined,
      variant: open ? dialogState.variant : undefined,
    });
    if (!open) {
      setSubmitted(false);
      form.reset(baseDefaultValuesRef.current);
    }
  };

  const onSubmit = (payload) => {
    if (loading || submitted) return;

    return mutationApi(submitMapping(payload));
  };

  const renderForm = () => {
    if (submitted) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-4 py-8 text-center">
          <CheckCircle className="size-12 text-green-500" />
          <h3 className="text-lg font-medium">
            Thank You! We&apos;ve received your message
          </h3>
          <Button onClick={() => setOpen(false)} className="mt-2">
            Close
          </Button>
        </div>
      );
    }

    return (
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {infoBoxText && <InfoBox>{infoBoxText}</InfoBox>}

          {children({ form, loading, submitted })}

          <ResponsiveDialogFooter>
            <Button
              type="submit"
              disabled={form.formState.isSubmitting}
              className="min-w-[90px]"
            >
              {form.formState.isSubmitting ? (
                <span className="flex gap-2">
                  <Loader2 className="size-4 animate-spin" />
                  Sending
                </span>
              ) : (
                "Send"
              )}
            </Button>
          </ResponsiveDialogFooter>
        </form>
      </Form>
    );
  };

  return (
    <ResponsiveDialog open={open} onOpenChange={setOpen}>
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>{title}</ResponsiveDialogTitle>
        </ResponsiveDialogHeader>

        {renderForm()}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
};

export default MessageDialog;
